/**
 * @typedef {Object} I18nInflectionEntry
 * @property {string} one
 * @property {string} other
 * @property {string} [zero]
 * @property {string} [two]
 * @property {string} [few]
 * @property {string} [many]
 */

/**
 * An object of locale keys to inflection entries.
 * @typedef {Object<string, I18nInflectionEntry>} I18nInflectionEntries
 */

/**
 * @typedef {Object} I18nInflections
 * @property {Object<string, I18nInflectionEntries>} __INFLECTIONS__
 */

/**
 * @typedef {Object<string, string[]>} I18nTranslationEntry
 */

/**
 * An object of locale keys to translation entries.
 * @typedef {Object<string, I18nTranslationEntry>} I18nTranslationEntries
 */

/**
 * @typedef {Object<string, I18nTranslationEntries>} I18nTranslations
 */

/**
 * A tagged template literal function for internationalization.
 * @param {string[]} strings
 * @param  {...string} values
 * @property {string} $locale
 * @property {object} db
 * @property {(...string) => i18n} annotated
 * @property {(string, number) => string} inflect
 * @returns {string}
 */
function i18n(strings, ...values) {
  const key = typeof strings === 'string' ? strings : strings.join('\x01')
  const translation = (i18n.db[key] && i18n.db[key][i18n.locale]) || strings
  const stringCount = translation.length
  let result = ''
  for (let i = 0; i < stringCount; i += 1) {
    result += translation[i]
    // drop null and undefined values to avoid printing null/undefined in the final string
    if (values[i] != null) {
      result += values[i]
    }
  }
  // return translation.map((string, idx) => [string, values[idx]])
  //   .reduce((acc, val) => acc.concat(val), [])
  //   .join('')
  return result
}

// Defaults
let pluralRules
Object.defineProperty(i18n, 'locale', {
  get: () => i18n.$locale,
  set: locale => {
    i18n.$locale = locale
    pluralRules = new Intl.PluralRules(locale)
  }
})
/**
 * @type {I18nInflections & I18nTranslations}
 */
i18n.db = { __INFLECTIONS__: {} }
i18n.locale = 'en'

/**
 * Has no runtime effect, but can be used to document a string for translation.
 * @param {string} description
 * @returns {i18n}
 * @example
 * i18n.annotated('This is a special one.')`Special 1: ${specialOne}`
 */
// eslint-disable-next-line no-unused-vars
i18n.annotated = description => i18n
i18n.inflect = (count, singular, plural) => {
  // eslint-disable-next-line no-underscore-dangle
  const config = '__INFLECTIONS__' in i18n.db && i18n.db.__INFLECTIONS__[singular]
  const locale = i18n.locale

  const pluralizationRule = pluralRules.select(count)
  if (!config || !(locale in config) || !(pluralizationRule in config[locale])) {
    return pluralizationRule === 'one' ? singular : plural
  }
  return config[locale][pluralizationRule] ?? config[locale].other ?? plural
}

export default i18n
